import { Address, beginCell, toNano } from '@ton/core';
import { API_BASE } from './config';

/**
 * The shared mini-games bank (Vault) and its LP PRMD jetton. Liquidity is
 * provided with a DepositBank message (the attached GRAM, net of a fixed gas
 * budget, is priced into LP shares) and withdrawn by burning LP at the
 * holder's jetton wallet — the vault redeems amount * bank / totalShares.
 * See contract/code_contracts/vault/Vault.tact.
 */
export const VAULT_ADDRESS: string =
  (import.meta.env?.VITE_VAULT_ADDRESS as string | undefined) ||
  'EQAWYYRCu_zK_ZgatBHd5Mp6hXSSgwntYUpSxdnWZfPCZnUz';

export const LP_MASTER_ADDRESS: string =
  (import.meta.env?.VITE_LP_MASTER_ADDRESS as string | undefined) ||
  'EQBK7jecuHonzGBwrJstA06o9NNQhrxyI1YjmbvELUv-eN6H';

// Gas math mirrored from house/constants.tact: a deposit pays
// HOUSE_LP_RESOLVE_VALUE (0.3) + HOUSE_LP_DEPOSIT_GAS (6·0.05 + 0.05 = 0.35)
// off the top; everything above that becomes shares.
export const DEPOSIT_GAS_TON = 0.65;

/** Smallest deposit the Vault accepts net of gas (HOUSE_DEPOSIT_MIN). */
export const MIN_DEPOSIT_GRAM = 1;
/** Attached to the LP burn: funds the wallet burn round trip and the payout send. */
export const WITHDRAW_GAS_TON = '0.15';
/** Attached to a ClaimRefShare: the claimant's own TON pays the transaction. */
export const CLAIM_GAS_TON = '0.08';

export interface VaultState {
  bankroll: bigint;
  maxWin: bigint;
  minBet: bigint;
  totalShares: bigint;
  refPool: bigint;
  totalBets: bigint;
  totalWagered: bigint;
  totalPaidOut: bigint;
}

// All chain reads go through our backend (backend/src/chain-reads.ts) — the
// browser never talks to a blockchain provider directly.
async function api<T>(path: string): Promise<T> {
  const r = await fetch(`${API_BASE}${path}`);
  if (!r.ok) throw new Error(`${path} → ${r.status}`);
  return (await r.json()) as T;
}

/** Decoded vaultState getter, served from our backend. */
export async function fetchVaultState(): Promise<VaultState> {
  const s = await api<Record<keyof VaultState, string>>('/api/vault/state');
  return {
    bankroll: BigInt(s.bankroll),
    maxWin: BigInt(s.maxWin),
    minBet: BigInt(s.minBet),
    totalShares: BigInt(s.totalShares),
    refPool: BigInt(s.refPool),
    totalBets: BigInt(s.totalBets),
    totalWagered: BigInt(s.totalWagered),
    totalPaidOut: BigInt(s.totalPaidOut),
  };
}

/** Unclaimed referral GRAM accumulated for `wallet` on the vault. */
export async function fetchRefBalance(wallet: string): Promise<bigint> {
  const j = await api<{ balanceNano: string }>(
    `/api/vault/ref-balance/${encodeURIComponent(wallet)}`,
  );
  return BigInt(j.balanceNano);
}

export interface LpPosition {
  balance: bigint; // LP units (9 decimals)
  walletAddress: string | null; // the holder's LP jetton wallet
}

/** The holder's LP PRMD balance and jetton-wallet address (our backend). */
export async function fetchLpPosition(owner: string): Promise<LpPosition> {
  try {
    const j = await api<{ balance: string; walletAddress: string | null }>(
      `/api/wallet/jetton/${encodeURIComponent(owner)}/${encodeURIComponent(LP_MASTER_ADDRESS)}`,
    );
    return { balance: BigInt(j.balance), walletAddress: j.walletAddress };
  } catch {
    return { balance: 0n, walletAddress: null };
  }
}

/** The wallet's native GRAM balance in nano (our backend). */
export async function fetchGramBalance(owner: string): Promise<bigint> {
  try {
    const j = await api<{ balanceNano: string }>(
      `/api/account-balance/${encodeURIComponent(owner)}`,
    );
    return BigInt(j.balanceNano);
  } catch {
    return 0n;
  }
}

/**
 * DepositBank{queryId, refKey}: the attached GRAM (net of the fixed gas
 * budget) buys LP shares — 95% minted to the depositor, 5% to the referral
 * chain. `amountStr` is the TOTAL attached GRAM including the gas budget.
 */
export function buildDeposit(amountStr: string, refKey?: number | null) {
  const effectiveRefKey =
    refKey != null && Number.isInteger(refKey) && refKey >= 0 ? refKey : 0;
  const body = beginCell()
    .storeUint(0x484f550e, 32)
    .storeUint(BigInt(Date.now()), 64)
    .storeUint(BigInt(effectiveRefKey), 64)
    .endCell();
  return {
    validUntil: Math.floor(Date.now() / 1000) + 300,
    messages: [
      {
        address: VAULT_ADDRESS,
        amount: toNano(amountStr).toString(),
        payload: body.toBoc().toString('base64'),
      },
    ],
  };
}

/**
 * TEP-74 LpBurn at the holder's LP jetton wallet — the only way liquidity
 * leaves the bank; the vault pays amount * bank / totalShares back.
 */
export function buildLpBurn(lpWallet: string, amount: bigint, owner: string) {
  const body = beginCell()
    .storeUint(0x595f07bc, 32)
    .storeUint(BigInt(Date.now()), 64)
    .storeCoins(amount)
    .storeAddress(Address.parse(owner))
    .storeMaybeRef(null)
    .endCell();
  return {
    validUntil: Math.floor(Date.now() / 1000) + 300,
    messages: [
      {
        address: lpWallet,
        amount: toNano(WITHDRAW_GAS_TON).toString(),
        payload: body.toBoc().toString('base64'),
      },
    ],
  };
}

/** ClaimRefShare{queryId}: sends the caller's accumulated referral balance. */
export function buildClaimRefShare() {
  const body = beginCell()
    .storeUint(0x484f5507, 32)
    .storeUint(BigInt(Date.now()), 64)
    .endCell();
  return {
    validUntil: Math.floor(Date.now() / 1000) + 300,
    messages: [
      {
        address: VAULT_ADDRESS,
        amount: toNano(CLAIM_GAS_TON).toString(),
        payload: body.toBoc().toString('base64'),
      },
    ],
  };
}

/** Parses a decimal string into 9-decimal base units without float rounding. */
export function parseUnits9(amountStr: string): bigint {
  const [ip = '0', fp = ''] = amountStr.split('.');
  return BigInt(ip || '0') * 1_000_000_000n + BigInt((fp + '000000000').slice(0, 9) || '0');
}

/** Formats 9-decimal base units for display (grouped, trimmed decimals). */
export function fmtUnits9(v: bigint, maxFrac = 2): string {
  const n = Number(v) / 1e9;
  return n.toLocaleString('en-US', { maximumFractionDigits: maxFrac });
}
